import { toast } from "@carbon6/c6-frontend-design";
import { notifyWindowCapReached, windowStack } from "@/app/windowStack";
import type { SubmitFormPrefill } from "./submit/sessionResubmitPrefill";
import { submitWizardFeatures } from "./submit/submitWizardFeatures";
import type { SubmitDatasetSelection } from "./submit/submitWizardForm";

// trainingSubmit kind 엔트리의 params 계약. 진입점이 폼에 넣는 시드만 있다. 위저드는 폼 상태를
// 내부에서 소유한다. 제출 뒤 후속 동작(상세 열기)도 위저드가 완료 toast 로 제안한다. 진입점마다
// 다르게 동작하면 같은 제출이 시작 위치에 따라 다르게 끝난다.
export type TrainingSubmitSessionWindowParams = {
  // 데이터셋이 이미 정해진 진입점(데이터셋 상세 [학습 시작] 등)이 폼의 초기 데이터셋으로 넣는다.
  initialDataset?: SubmitDatasetSelection;
  // 실패 세션의 "설정 고쳐 다시 제출" 진입점이 폼 전체를 채운다(ZERG-257). initialDataset 과
  // 함께 오지 않는다. 두 값의 진입점이 서로 다르다.
  prefill?: SubmitFormPrefill;
  mode?: "session";
};

// 시드를 `never` 로 막는다. 재제출 프리필은 원본 세션의 Commit 과 시작점을 포함한다. 그러나 Schedule
// 위저드는 그 두 축을 열지 않는다(`submitWizardFeatures`). 시드가 들어오면 화면에 보이지 않는 고정
// 값이 전문에 포함되어 나간다. 그래서 이 조합을 타입이 막는다.
export type TrainingSubmitScheduleWindowParams = {
  initialDataset?: never;
  prefill?: never;
  mode: "schedule";
};

// 이 창이 만드는 것. 값이 없으면 단건 세션 제출이다. "schedule" 이면 같은 위저드가 Schedule 스텝을
// 추가해 반복 스케줄을 만든다(key 가 달라 세션 제출 창과 함께 열릴 수 있다).
export type TrainingSubmitWindowParams =
  | TrainingSubmitSessionWindowParams
  | TrainingSubmitScheduleWindowParams;

// 새 학습(제출 위저드)을 전체 화면 창으로 연다. 한 번에 하나만 연다(같은 key="new"). 그래서 다시 열면
// 기존 창을 복원한다. cap(5)을 넘으면 열지 않고 toast 로 안내한다. 세션 창만 연다. 제목과 key 가
// 세션용으로 고정이라, mode 를 받으면 작업 표시줄과 창 안쪽이 다른 이름을 표시한다. Schedule 은
// 아래 전용 진입점을 쓴다.
export function openTrainingSubmitWindow(params: TrainingSubmitSessionWindowParams = {}): void {
  // 복원은 params 를 갱신하지 않는다(진행 중 폼 보호). 시드(initialDataset·prefill)를 전달했는데
  // 기존 창이 있으면 시드가 무시된다. 그래서 알림 없이 다른 값이 선택된 상태가 되지 않도록 toast 로
  // 알린다. 스케줄 창(key "new-schedule")도 같은 kind 를 쓰므로 key 까지 비교한다.
  const restoringWithSeed =
    (params.initialDataset !== undefined || params.prefill !== undefined) &&
    windowStack.entryOf("trainingSubmit", "new") !== undefined;
  const id = windowStack.open({
    kind: "trainingSubmit",
    key: "new",
    title: submitWizardFeatures("session").windowTitle,
    params,
  });
  if (id === null) {
    notifyWindowCapReached();
    return;
  }
  if (restoringWithSeed) {
    toast.info(
      "진행 중인 새 Training 창을 복원했습니다. 방금 고른 시작값은 반영되지 않았으니, 이어서 작성하거나 창을 닫고 다시 열어 주세요.",
    );
  }
}

// 새 Schedule(제출 위저드의 schedule mode)을 전체 화면 창으로 연다. 새 Training 창과 key 가 달라
// 함께 열릴 수 있다. 시드 진입점이 없으므로(목록 헤더 버튼 하나) 복원 안내도 없다.
export function openTrainingScheduleWindow(): void {
  const id = windowStack.open({
    kind: "trainingSubmit",
    key: "new-schedule",
    title: submitWizardFeatures("schedule").windowTitle,
    params: { mode: "schedule" } satisfies TrainingSubmitWindowParams,
  });
  if (id === null) {
    notifyWindowCapReached();
  }
}
